import React from 'react';
import {
  ThunderboltOutlined,
  CloudOutlined,
  LockOutlined,
  TeamOutlined,
  DashboardOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import { triageColors } from '../theme/antd-theme';
import { TICKET_CLASS_IDS } from '../types/triage';

// Shared tag/color/icon maps used by both TicketListPane and the upcoming
// IssueListPane. Pure constants, no hooks, safe to import anywhere.

export const nodeTypeIcon: Record<string, React.ReactNode> = {
  energy: <ThunderboltOutlined style={{ color: '#FF8200' }} />,
  energy_switchless: <ThunderboltOutlined style={{ color: '#FF8200' }} />,
  aircon: <CloudOutlined style={{ color: '#006EB7' }} />,
  aircon_acir: <CloudOutlined style={{ color: '#006EB7' }} />,
  door: <LockOutlined style={{ color: '#8c8c8c' }} />,
  occupancy: <TeamOutlined style={{ color: '#00A651' }} />,
  compressor: <DashboardOutlined style={{ color: '#722ed1' }} />,
  pipe: <NodeIndexOutlined style={{ color: '#13c2c2' }} />,
};

export const nodeTypeLabel: Record<string, string> = {
  energy: 'Energy node',
  energy_switchless: 'Energy node (switchless)',
  aircon: 'Aircon node',
  aircon_acir: 'Aircon node',
  door: 'Door node',
  occupancy: 'Occupancy node',
  compressor: 'Compressor node',
  pipe: 'Pipe node',
};

export const classTagMap: Record<number, { label: string; color: string }> = {
  [TICKET_CLASS_IDS.BATTERY_DRAINED]: { label: 'BAT', color: triageColors.batteryDrained },
  [TICKET_CLASS_IDS.OFFLINE_UNKNOWN]: { label: 'UNK', color: triageColors.offlineUnknown },
  [TICKET_CLASS_IDS.HARDWARE_FAULT]: { label: 'HW', color: triageColors.hardwareFault },
  [TICKET_CLASS_IDS.HARDWARE_FAULT_EOL]: { label: 'EOL', color: triageColors.hardwareFaultEol },
  [TICKET_CLASS_IDS.TAMPERING]: { label: 'TAMP', color: triageColors.actionTampering },
  [TICKET_CLASS_IDS.FIRMWARE_ISSUE]: { label: 'FW', color: triageColors.actionFirmware },
  [TICKET_CLASS_IDS.OUT_OF_RANGE]: { label: 'OOR', color: triageColors.actionOutOfRange },
  [TICKET_CLASS_IDS.ON_SITE_INVESTIGATION]: { label: 'OSI', color: triageColors.actionOnSite },
  [TICKET_CLASS_IDS.FALSE_ALERT]: { label: 'FA', color: triageColors.actionFalseAlert },
};

export const classTooltipMap: Record<number, string> = {
  [TICKET_CLASS_IDS.BATTERY_DRAINED]: 'Battery Drained',
  [TICKET_CLASS_IDS.OFFLINE_UNKNOWN]: 'Offline — Unknown cause',
  [TICKET_CLASS_IDS.HARDWARE_FAULT]: 'Hardware Fault',
  [TICKET_CLASS_IDS.HARDWARE_FAULT_EOL]: 'Hardware Fault — End of Life',
  [TICKET_CLASS_IDS.TAMPERING]: 'Tampering / Damage',
  [TICKET_CLASS_IDS.FIRMWARE_ISSUE]: 'Firmware Issue',
  [TICKET_CLASS_IDS.OUT_OF_RANGE]: 'Out of Range (inferred — verify position on-site; based on a recent move or weak pre-offline signal, not a confirmed measurement)',
  [TICKET_CLASS_IDS.ON_SITE_INVESTIGATION]: 'On-site Investigation',
  [TICKET_CLASS_IDS.FALSE_ALERT]: 'False Alert',
};

export const priorityColor: Record<number, string> = {
  1: triageColors.priorityP1,
  2: triageColors.priorityP2,
  // P3 fallback uses the design token closest to "medium" — without this,
  // backend rows with priority=3 rendered a no-color span (UX-037).
  3: triageColors.priorityP2,
  4: triageColors.priorityP4,
};

export const priorityLabel: Record<number, string> = {
  1: 'Priority 1 — Critical',
  2: 'Priority 2 — High',
  3: 'Priority 3 — Medium',
  4: 'Priority 4 — Low',
};

// Advisory-claim staleness horizon. Mirrors CLAIM_TTL_MINUTES in
// server/src/routes/issues.ts — a claim older than this is abandoned, not
// in-flight, so the UI stops showing its lock/soft-lock. Single source so the
// list, detail, worklist, and team board all agree.
export const CLAIM_TTL_MINUTES = 30;
export function isClaimFresh(claimedAt: string | null | undefined): boolean {
  if (!claimedAt) return false;
  return Date.now() - new Date(claimedAt).getTime() < CLAIM_TTL_MINUTES * 60_000;
}

// Short display for an actor string (claim holder / last-triager). Emails
// collapse to the local-part ("mark@sensorflow.org" → "mark"); Auth0 subs
// ("auth0|abc123") collapse to a stable short id. Used on space-tight rows.
export function shortActor(actor: string | null | undefined): string {
  if (!actor) return 'system';
  if (actor.includes('@')) return actor.split('@')[0];
  if (actor.includes('|')) {
    const tail = actor.split('|').pop() || actor;
    return `${actor.split('|')[0]}:${tail.slice(-4)}`;
  }
  return actor;
}

// --- Task status badge map (sensorflow.tasks.status enum) ---
// Centralized so WorkTasksPage, ActionsPage, and any future task-aware
// surface render the same color for the same status (UX-036).
//
// Color values are antd preset names so <Tag color={...}> renders them as
// the antd preset palette — consistent with the existing in-app palette
// and accessible (antd tunes contrast for these names).
export const taskStatusBadgeMap: Record<string, { color: string; label: string }> = {
  'pending': { color: 'blue', label: 'Pending' },
  'in-progress': { color: 'orange', label: 'In Progress' },
  'accepted': { color: 'cyan', label: 'Accepted' },
  'done': { color: 'green', label: 'Done' },
  'canceled': { color: 'default', label: 'Canceled' },
  'auto-resolved': { color: 'purple', label: 'Auto-resolved' },
};

// "Days since last seen" coloring used by issue/ticket list rows.
// v4 (UX-029): tightened the warning threshold from >=7d → >=2d. A node
// silent for 6 days should not visually read as "fine".
export function freshnessChipForDaysSince(days: number | null): {
  color: 'default' | 'red' | 'orange' | 'gold';
  label: string;
} {
  if (days == null) return { color: 'default', label: 'unknown' };
  if (days >= 30) return { color: 'red', label: `silent ${days}d` };
  if (days >= 7) return { color: 'orange', label: `silent ${days}d` };
  if (days >= 2) return { color: 'gold', label: `silent ${days}d` };
  return { color: 'default', label: `silent ${days}d` };
}

// Used as the chip color for the "PEND" / unclassified case. Without an
// explicit value, the antd Tag falls back to the default neutral and looks
// identical to other informational tags around it (UX-025).
export const PENDING_CLASS_TAG = { label: 'PEND', color: triageColors.offlineUnknown };

// Sub-class tags for OFFLINE_UNKNOWN (class 10). Each value is the
// weak-evidence sister of an existing top-level class — the chip color
// mirrors the sister class so operators can pattern-match at a glance
// (tamper_suspect orange ↔ TAMPERING orange, etc.). Tooltip says
// "leaning <class>" so it reads as a directional hint, not a commitment.
export const subclassTagMap: Record<
  string,
  { label: string; color: string; tooltip: string }
> = {
  tamper_suspect: {
    label: 'tamper?',
    color: triageColors.actionTampering,
    tooltip: 'Leaning TAMPERING — cluster of nodes at same site dropped together with healthy precursors',
  },
  fw_suspect: {
    label: 'fw?',
    color: triageColors.actionFirmware,
    tooltip: 'Leaning FIRMWARE — same fw version cohort spike, or fast recovery with no OTA',
  },
  hw_drain: {
    label: 'hw drain?',
    color: triageColors.hardwareFault,
    tooltip: 'Leaning HW_FAULT — battery forecast flagged excess current draw',
  },
  signal_drift: {
    label: 'signal?',
    color: triageColors.actionOutOfRange,
    tooltip: 'Leaning OUT_OF_RANGE — pre-offline signal trending degraded but above weak threshold',
  },
};
